import { SettingList } from '@sparklayer-io/spark-dash-pages/components/setting-list';
import { SettingRow } from '@sparklayer-io/spark-dash-pages/components/setting-row';
import { Button, Card, ExternalLink, Icon } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useEnableMetafield } from '../hooks/use-enable-metafield';
import type { Metafield, MetafieldGroup } from '../types';

const METAFIELDS_I18N = 'features:platform.pages.metafields_page' satisfies I18nPrefix;

function displayGroupName(name: MetafieldGroup['name']): string {
  switch (name) {
    case 'PRODUCT_VARIANT':
      return t(`${METAFIELDS_I18N}.group_product_variant`);
    case 'CUSTOMER':
      return t(`${METAFIELDS_I18N}.group_customer`);
    case 'ORDER':
      return t(`${METAFIELDS_I18N}.group_order`);
    default:
      return name;
  }
}

function MetafieldRow({ metafield, isFetching }: { metafield: Metafield; isFetching: boolean }) {
  const enableMetafield = useEnableMetafield();

  const isEnabling =
    enableMetafield.isPending ||
    (enableMetafield.isSuccess && metafield.state !== 'enabled' && isFetching);

  return (
    <SettingRow
      align="center"
      label={
        <span className="flex items-center gap-2">
          {metafield.schema.name}
          <ExternalLink link={metafield.schema.documentationUrl} ariaLabel={metafield.schema.name}>
            <Icon name="infoCircle" size="sm" />
          </ExternalLink>
        </span>
      }
      description={metafield.schema.description}
      control={
        metafield.state === 'enabled' ? (
          <Icon name="checkCircle" size="md" />
        ) : (
          <Button
            variant="secondary"
            loading={isEnabling}
            onClick={() => enableMetafield.mutate(metafield.schema.key)}
          >
            {t(`${METAFIELDS_I18N}.enable`)}
          </Button>
        )
      }
    />
  );
}

export function MetafieldGroupCard({
  group,
  isFetching,
}: {
  group: MetafieldGroup;
  isFetching: boolean;
}) {
  return (
    <Card initialCollapsed={false}>
      <Card.Header headingLevel="h2" collapsible>
        {displayGroupName(group.name)}
      </Card.Header>
      <Card.Body>
        <SettingList>
          {group.metafields.map((metafield) => (
            <MetafieldRow
              key={metafield.schema.key}
              metafield={metafield}
              isFetching={isFetching}
            />
          ))}
        </SettingList>
      </Card.Body>
    </Card>
  );
}
